// МАСТЕР ЗАПУСКА — INSTAGRAM (TRIGGER-WIZARD, заказ владельца 15.08.2026).
//
// Тот же мастер, что у Телеграма (trigger-tg): карточка шаблона БЕЗ своего мастера
// открывает этот, и он спрашивает только аккаунт и «Что запускает». Содержимое бота
// приходит из шаблона и анкетой не трогается.
//
// С 13.09.2026 (BACKLOG п.24, план TRIGGERS-CHAINS-FIX Э7) шаг «Что запускает» — ТОТ ЖЕ
// шаг-список, что у «Подарка за подписку» (triggerStep, kinds 'ig'): все ШЕСТЬ событий
// Инстаграма, включая «Реакцию на историю», которой прежний свой список не знал. Второго
// списка событий у продукта нет. Старые черновики (поля `entryEvent`/`startMode`/
// `keywords`/`postsMode`/`mediaIds`) открывает адаптер.
//
// Мастер один на КАНАЛ: новый инста-шаблон получает его сам, заводить ничего не надо.
import { readChatStart, writeChatStart, type WorkflowJson } from '@scriptera/ui/scenario';
import { botEntry, BOT_ENTRY_ALWAYS_ANY, type BotEntry, type BotEntryEvent } from '@scriptera/db/bot-entries';
import { ansMediaIds, ansStr, ansWords, wizAccountParam, wizEntryWords, type TemplateWizardDef, type WizardLocale } from './core';
import { botTrigChat, botTrigStore, TRIG_IG_EVENTS, trigBlocked, triggerStep } from './trigger-step';

/** Ключ шага «Что запускает» (по нему stepBlocked узнаёт, что сейчас на экране). */
const IG_START_STEP = 'entry';

/**
 * СТАРЫЙ ЧЕРНОВИК (до 13.09.2026): один вход полями `entryEvent`, `startMode`, `keywords`,
 * `postsMode`, `mediaIds`. Ключа `entries` нет — раскладываем эти поля в один вход;
 * незнакомое событие — личка (так вёл себя узел до списка событий: рабочий сценарий, а не
 * мёртвый). Ответов нет вовсе — тоже личка: у мастера запуска Инстаграма событие есть
 * всегда (trigger-wizards.test).
 */
function igTriggerLegacyEntries(a: Record<string, unknown>): BotEntry[] {
  const rawEvent = ansStr(a, 'entryEvent', 20, 'message');
  const event = (TRIG_IG_EVENTS.includes(rawEvent as BotEntryEvent) ? rawEvent : 'message') as BotEntryEvent;
  const always = (BOT_ENTRY_ALWAYS_ANY as readonly string[]).includes(event);
  const mode = always ? 'any' : ansStr(a, 'startMode', 16, 'any');
  const keywords = ansWords(a, 'keywords');
  const byWord = (mode === 'keyword' || mode === 'exact') && keywords.length > 0;
  const media = event === 'comment' && ansStr(a, 'postsMode', 8, 'all') === 'some' ? ansMediaIds(a) : [];
  return [
    botEntry(event, byWord ? (mode === 'exact' ? 'exact' : 'keyword') : 'any', {
      keywords: byWord ? keywords : [],
      mediaIds: media,
    }),
  ];
}

const store = botTrigStore('ig', igTriggerLegacyEntries);

export const triggerIg: TemplateWizardDef = {
  id: 'trigger-ig',
  templateSource: '',
  channelWizard: true,
  accountChannel: 'instagram',

  // ЗНАКОМСТВО (ТЗ-2 З1). Мастер запуска один на всю сеть и про конкретный шаблон
  // ничего не знает — имя и описание бота в это знакомство вставляет карточка каталога
  // (wizWithLead в bot-templates); здесь запасной текст.
  // Чат на первом экране — сцена «Слово в комментарии» с примеров работ (ТЗ-3 Т1).
  demoScene: 's2',

  lead(locale: WizardLocale) {
    const ru = locale === 'ru';
    return {
      title: ru ? 'Новый бот в Instagram' : 'New Instagram bot',
      text: ru
        ? 'Бот уже собран: сообщения и ответы готовы. Осталось указать, в каком аккаунте он работает и с чего начинается разговор.'
        : 'The bot is already assembled: the messages and replies are ready. Specify which account it works in and what starts the conversation.',
    };
  },

  steps(locale: WizardLocale) {
    const ru = locale === 'ru';
    return [
      {
        key: 'account',
        title: ru ? 'Аккаунт' : 'Account',
        intro: ru
          ? 'Выберите аккаунт, в котором будет работать бот, и укажите, с чего начинается разговор. Сообщения уже готовы, их можно изменить после создания.'
          : 'Select the account for the bot and specify what starts the conversation. The messages are already prepared; you can edit them after creation.',
        params: [wizAccountParam('instagram', locale)],
      },
      // ШАГ 2 — «Что запускает»: ТОТ ЖЕ шаг-список, что у «Подарка за подписку» — шесть
      // событий Инстаграма, у каждого свои настройки или их нет.
      triggerStep<BotEntry>(locale, {
        kinds: 'ig',
        key: IG_START_STEP,
        title: ru ? 'Что запускает' : 'What starts it',
        intro: ru
          ? 'Выберите события, которые запускают бота: сообщение в директ, комментарий, эфир, ответ на историю, реакция или отметка в ней. Можно выбрать несколько.'
          : 'Select the events that start the bot: a direct message, a comment, a live, a story reply, a reaction or a mention in a story. You can select several.',
        store,
        chat: botTrigChat,
      }),
    ];
  },

  // Превью-телефон показывает ТО, ЧТО МАСТЕР НАСТРАИВАЕТ, — как человек запустит бота.
  // Реплики бота придумывать нельзя: они живут в шаблоне и анкете не видны. Одинокое
  // сообщение бота предпросмотр рисует плашкой-подсказкой — пустого экрана нет.
  preview(answers, _stepIdx, locale) {
    const ru = locale === 'ru';
    const first = store.read(answers)[0];
    if (!first) return [{ from: 'bot', text: ru ? 'Выберите, что запускает бота' : 'Select what starts the bot' }];
    if (first.event === 'storyMention' || first.event === 'storyReaction' || first.event === 'storyReply') {
      const text =
        first.event === 'storyMention'
          ? ru
            ? 'Запуск: пользователь отметил ваш аккаунт в своей истории'
            : 'Started by a mention of your account in a story'
          : first.event === 'storyReaction'
            ? ru
              ? 'Запуск: пользователь поставил реакцию на вашу историю'
              : 'Started by a reaction to your story'
            : ru
              ? 'Запуск: пользователь ответил на вашу историю'
              : 'Started by a reply to your story';
      if (first.kind === 'any') return [{ from: 'bot', text }];
    }
    if (first.kind === 'keyword' || first.kind === 'exact') {
      const word = first.keywords[0] ?? '';
      if (!word) {
        return [{ from: 'bot', text: ru ? 'Добавьте кодовое слово, и оно появится здесь' : 'Add a code word and it will appear here' }];
      }
      return [{ from: 'user', text: word }];
    }
    return [{ from: 'user', text: ru ? 'Привет!' : 'Hi!' }];
  },

  // Содержимое бота — из шаблона; анкета правит ОДНО: список событий в узле запуска.
  // Именно из него диспетчер берёт trigger_event маршрута (bot-dispatch,
  // instagramDispatchInfo). Правка иммутабельная, параметры пишет общий writeChatStart.
  buildWorkflow(template: WorkflowJson, answers: Record<string, unknown>): WorkflowJson {
    const events = [...new Set(store.read(answers).map((e) => e.event))];
    return {
      ...template,
      nodes: template.nodes.map((n) =>
        readChatStart(n)?.channel === 'instagram'
          ? { ...n, parameters: writeChatStart(n, { channel: 'instagram', events: events.length > 0 ? events : ['message'] }) }
          : n,
      ),
    };
  },

  triggerMeta(answers) {
    return { entries: store.read(answers) };
  },

  // Предохранитель от копий (409 bot_exists): каждый вход — со своим типом события.
  duplicateStarts(answers) {
    return store.read(answers);
  },

  stepBlocked(answers, stepKey, locale) {
    if (stepKey !== IG_START_STEP) return null;
    const list = store.read(answers);
    if (list.length === 0) return wizEntryWords(locale).empty;
    return trigBlocked('ig', locale, list, answers);
  },
};
